import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import PropTypes from 'prop-types';
import { toNumber, sumBy } from 'lodash';
import { Row, Col, Button, TabContent, TabPane } from 'reactstrap';
import { animateScroll } from 'react-scroll';

import SummaryModal from '../Vendor/Modal';
import BZAPIService from '../../../services/BZAPIService';
import ProductPriority from '../section/ProductPriority';
import TabNav from '../section/TabNav';
import TabOne from '../section/TabOne';
import DefaultConfigTab from '../section/DefaultConfigTab';
import HistoryTab from '../section/HistoryTab';
import RiskScore from '../section/RiskScore';

// constants
import SETTINGS from '../../../constants/settings';

const ConfigFormRedHat = ({
  settings,
  isReadOnly,
  vendor,
  vendorLabel,
  vendorId,
  bugsDateBack,
  setBugsDateBack,
  snCompanySysId,
  setSnCompanySysId,
  vendorPriorityOptions,
  vendorStatusOptions,
  vendorResolutionOptions,
  snIntegrationCheck,
  services,
  vendors,
  priorityStats,
  snCompanySysIdOptions,
  snCompanySysIdErrors,
  setVendorResolutions,
  vendorResolutions,
  setVendorStatuses,
  vendorStatuses,
  fields,
  handleFieldChange,
  priorityMaps,
  setPriorityMaps,
  snPriorityOptions,
  keywords,
  setKeywords,
  searchFields,
  setSearchFields,
  setServices,
  setVendors,
  buildVendorStat,
  setPriorityStats,
  setBugs,
  isConfigured,
  isCve,
  setIsCve,
  syncOnly,
  setSyncOnly,
  loading,
  excludedComponents,
  setExcludedComponents,
  bugRisk,
  setBugRisk,
  isServiceActive,
  setIsServiceActive,
  allRequired,
  setAllRequired,
  isUpdated,
  setIsUpdated
}) => {
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const [includedComponents, setIncludedComponents] = useState([]);
  // const [excludedComponents, setExcludedComponents] = useState([]);
  const [loader, setLoader] = useState(false);

  const [query, setQuery] = useState('');
  const [offset, setOffset] = useState('0');
  const [limit, setLimit] = useState('2500');

  const [activeTab, setActiveTab] = useState('1');
  const [modalOpen, setModalOpen] = useState(false);
  const [validated, setValidated] = useState(null);
  const [isForceRun, setIsForceRun] = useState(false);
  const [nextRun, setNextRun] = useState();
  const [timeUnit, setTimeUnit] = useState('');
  const [timeValue, setTimeValue] = useState({});

  const [riskError, setRiskError] = useState('');

  async function setBugServiceFrequency() {
    // Check for time unit, post frequency
    const data = {
      timeUnit,
      number: toNumber(timeValue.value)
    };
    if (vendorId.length) {
      try {
        const res = await BZAPIService.postBugServiceSchedule(vendorId, data, { method: 'POST' });
        setNextRun(res?.data?.nextRunUTC);
        // console.log('res', res);
      } catch (e) {
        console.log(e);
      }
    }
  }

  const navigate = useNavigate();

  async function onLoad() {
    setLoader(true);
    try {
      let components = (await BZAPIService.getComponents(vendorId, query, offset, limit)) || [];

      settings?.excludedBugComponents
        ? setExcludedComponents(settings.excludedBugComponents)
        : setExcludedComponents([]);

      // setIncludedComponents(defaults.vendorData.vendorBugComponents);
      settings.searchFields
        ? setSearchFields(settings.searchFields)
        : setSearchFields(vendor?.vendorData?.searchFields);

      setIncludedComponents(components ? components.results : []);

      setLoader(false);
    } catch (e) {
      console.log(e);
      setLoader(false);
    }
  }

  useEffect(() => {
    onLoad();
  }, []);

  useEffect(() => {
    setIsUpdated(true);
    if (
      bugsDateBack?.length &&
      snCompanySysId?.value &&
      priorityMaps?.length &&
      vendorStatuses.length &&
      !priorityMaps.some(mapping => !mapping.snPriority || !mapping.vendorPriority)
    ) {
      setAllRequired(true);
    } else {
      setAllRequired(false);
    }
  }, [
    fields,
    bugsDateBack,
    vendorStatuses,
    priorityMaps,
    keywords,
    searchFields,
    snCompanySysId,
    isCve,
    vendorResolutions,
    excludedComponents,
    bugRisk
  ]);

  async function saveConfig() {
    console.log('saveConfig', fields);
    const vendorPriorities = priorityMaps.map(mapping => {
      return {
        value: mapping.vendorPriority.value,
        label: mapping.vendorPriority.value,
        snPriority: mapping.snPriority.value
      };
    });

    const data = {
      bugsDateBack: bugsDateBack,
      snAffectedCIQuery: fields.snAffectedCIQuery,
      filterCVEs: isCve,
      vendorPriorities,
      vendorStatuses,
      vendorBugResolutions: vendorResolutions,
      snCompanySysId: snCompanySysId.value ? snCompanySysId.value : fields.snCompanySysId,
      excludedBugComponents: excludedComponents,
      type: SETTINGS.VENDOR,
      excludedKws: keywords,
      searchFields: searchFields,
      bugRiskScore: bugRisk
    };

    return BZAPIService.saveSetting(vendorId, syncOnly, data, isForceRun);
  }

  async function handleSave() {
    setRiskError('');
    try {
      const filteredPriorities = priorityMaps.filter(mapping => {
        return mapping?.vendorPriority?.value && mapping?.snPriority?.value;
      });
      setPriorityMaps(filteredPriorities);

      setIsLoading(true);
      setIsSubmitted(true);
      // Validate
      if (!bugsDateBack.length || !snCompanySysId) {
        setValidated(false);
        setActiveTab('3');
        setIsLoading(false);
        return;
      }

      if (!vendorStatuses.length || !priorityMaps.length) {
        setIsLoading(false);
        setValidated(false);
        setActiveTab('4');
        return;
      }

      // Validate Risk Score
      const activeRisk = bugRisk.filter(y => {
        return y.active;
      });
      const activeSum = sumBy(activeRisk, risk => {
        return risk.value;
      });

      if (activeSum < 100 && activeSum > 1) {
        console.log('Bug risk share is less than 100');
        setValidated(false);
        setActiveTab('5');
        setIsLoading(false);
        setRiskError('Bug risk share total must be 100');
        return;
      }

      await saveConfig();
      setBugServiceFrequency();
      setIsLoading(false);
      setIsSubmitted(false);
      setModalOpen(false);
      setIsForceRun(false);
      setActiveTab('1');
      toast.success(`Settings Saved!`);
    } catch (e) {
      setIsLoading(false);
      toast.error('Failed to save.');
      console.log('e', e);
    }
  }

  function handleValidationError() {
    setModalOpen(!modalOpen);
    animateScroll.scrollTo(70);
  }

  return (
    <>
      <TabNav
        vendorId={vendorId}
        credentials={false}
        activeTab={activeTab}
        snIntegrationCheck={snIntegrationCheck}
        setActiveTab={setActiveTab}
        setIsLoading={setIsLoading}
        isLoading={isLoading}
        setServices={setServices}
        setVendors={setVendors}
        buildVendorStat={buildVendorStat}
        setPriorityStats={setPriorityStats}
        setBugs={setBugs}
        isConfigured={isConfigured}
        hasBugRisk={true}
      />

      <TabContent>
        <TabOne
          vendorId={vendorId}
          vendorLabel={vendorLabel}
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          snIntegrationCheck={snIntegrationCheck}
          services={services}
          vendors={vendors}
          priorityStats={priorityStats}
          isConfigured={isConfigured}
          modalOpen={modalOpen}
          setModalOpen={setModalOpen}
          setIsForceRun={setIsForceRun}
          timeUnit={timeUnit}
          setTimeUnit={setTimeUnit}
          timeValue={timeValue}
          setTimeValue={setTimeValue}
          nextRun={nextRun}
          setNextRun={setNextRun}
          loading={loading}
          isServiceActive={isServiceActive}
          setIsServiceActive={setIsServiceActive}
        />
        <DefaultConfigTab
          activeTab={activeTab}
          vendorLabel={vendorLabel}
          bugsDateBack={bugsDateBack}
          setBugsDateBack={setBugsDateBack}
          isSubmitted={isSubmitted}
          snCompanySysIdErrors={snCompanySysIdErrors}
          snCompanySysIdOptions={snCompanySysIdOptions}
          setSnCompanySysId={setSnCompanySysId}
          snCompanySysId={snCompanySysId}
          fields={fields}
          handleFieldChange={handleFieldChange}
        />
        <form noValidate style={{ minWidth: '100%' }}>
          <TabPane tabId="4">
            {activeTab === '4' && (
              <ProductPriority
                title="Default Bug Configuration"
                vendorPriorityOptions={vendorPriorityOptions}
                vendorStatusOptions={vendorStatusOptions}
                vendorResolutionOptions={vendorResolutionOptions}
                setVendorStatuses={setVendorStatuses}
                setVendorResolutions={setVendorResolutions}
                vendorStatuses={vendorStatuses}
                vendorResolutions={vendorResolutions}
                priorityMaps={priorityMaps}
                setPriorityMaps={setPriorityMaps}
                snPriorityOptions={snPriorityOptions}
                keywords={keywords}
                setKeywords={setKeywords}
                searchFields={searchFields}
                setSearchFields={setSearchFields}
                includedComponents={includedComponents}
                setIncludedComponents={setIncludedComponents}
                excludedComponents={excludedComponents}
                setExcludedComponents={setExcludedComponents}
                loader={loader}
                setLoader={setLoader}
                setQuery={setQuery}
                query={query}
                offset={offset}
                limit={limit}
                setOffset={setOffset}
                setLimit={setLimit}
                isCve={isCve}
                setIsCve={setIsCve}
                vendorId={vendorId}
                vendorLabel={vendorLabel}
              />
            )}
          </TabPane>
          <div style={{ minWidth: '100%' }}>
            <TabPane tabId="5">
              {activeTab === '5' && (
                <RiskScore bugRisk={bugRisk} setBugRisk={setBugRisk} riskError={riskError} loading={loading} />
              )}
            </TabPane>
          </div>
          <TabPane tabId="6">{activeTab === '6' && <HistoryTab vendorId={vendorId} />}</TabPane>

          <Row className={`${activeTab === '1' ? 'd-none' : ''} flex-between-center`}>
            <Col xs="auto">
              <Button
                block
                color="falcon-danger"
                onClick={() => {
                  console.log(`/vendors/${vendorId}/configuration`);
                  navigate(-1);
                }}
              >
                Cancel
              </Button>
            </Col>
            <Col xs="auto">
              <SummaryModal
                modalOpen={modalOpen}
                isReadOnly={isReadOnly}
                setModalOpen={setModalOpen}
                handleSave={handleSave}
                isLoading={isLoading}
                vendorLabel={vendorLabel}
                vendorId={vendorId}
                validated={validated}
                setValidated={setValidated}
                syncOnly={syncOnly}
                setSyncOnly={setSyncOnly}
                handleValidationError={handleValidationError}
                allRequired={allRequired}
                isUpdated={isUpdated}
              />
            </Col>
          </Row>
        </form>
      </TabContent>
    </>
  );
};
ConfigFormRedHat.propTypes = {
  vendor: PropTypes.object,
  isReadOnly: PropTypes.bool,
  settings: PropTypes.object,
  vendorLabel: PropTypes.string,
  vendorId: PropTypes.string,
  vendorPriorityOptions: PropTypes.array,
  vendorStatusOptions: PropTypes.array,
  vendorResolutionOptions: PropTypes.array,
  snCompanySysIdOptions: PropTypes.object,
  snCompanySysId: PropTypes.object,
  snIntegrationCheck: PropTypes.bool,
  services: PropTypes.array,
  vendors: PropTypes.array,
  bugs: PropTypes.object,
  priorityStats: PropTypes.object,
  setPriorityStats: PropTypes.func,
  snCompanySysIdErrors: PropTypes.bool,
  vendorEventScopes: PropTypes.array,
  setVendorEventScopes: PropTypes.func,
  vendorResolutions: PropTypes.array,
  setVendorResolutions: PropTypes.func,
  setVendorStatuses: PropTypes.func,
  vendorStatuses: PropTypes.array,
  fields: PropTypes.object,
  handleFieldChange: PropTypes.func,
  priorityMaps: PropTypes.array,
  setPriorityMaps: PropTypes.func,
  snPriorityOptions: PropTypes.array,
  keywords: PropTypes.array,
  setKeywords: PropTypes.func,
  searchFields: PropTypes.array,
  setSearchFields: PropTypes.func,
  setSnCompanySysId: PropTypes.func,
  bugsDateBack: PropTypes.string,
  setBugsDateBack: PropTypes.func,
  setServices: PropTypes.func,
  setVendors: PropTypes.func,
  buildVendorStat: PropTypes.func,
  setBugs: PropTypes.func,
  isConfigured: PropTypes.bool,
  isCve: PropTypes.bool,
  setIsCve: PropTypes.func,
  syncOnly: PropTypes.bool,
  setSyncOnly: PropTypes.func,
  loading: PropTypes.bool,
  excludedComponents: PropTypes.array,
  setExcludedComponents: PropTypes.func,
  bugRisk: PropTypes.array,
  setBugRisk: PropTypes.func,
  setIsServiceActive: PropTypes.func,
  isServiceActive: PropTypes.number,
  allRequired: PropTypes.bool,
  isUpdated: PropTypes.bool,
  setAllRequired: PropTypes.func,
  setIsUpdated: PropTypes.func
};
export default ConfigFormRedHat;
